/**
 * InspectionSection
 * 
 * Renders inspection-related tables including:
 * - Remarks
 * - Regulatory text
 * - Test/control information
 * - Inspection details (date, type, status, next exam)
 * - Status codes explanation
 */

import { jsPDF } from 'jspdf'
import {
  CONTROL_CARD_CONFIG,
  CONTROL_CARD_TEXT,
  JOB_TYPE_TRANSLATIONS,
  STATUS_MAPPINGS,
} from '../../config'
import { TableRenderer } from '../../infrastructure'
import type { ControlCardInspection, ControlCardEquipment } from '../../infrastructure'
import { DateFormattingService } from '../../domain'

/**
 * Inspection section renderer
 */
export class InspectionSection {
  private readonly tableRenderer: TableRenderer
  private readonly dateFormatter: DateFormattingService

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.tableRenderer = new TableRenderer(config)
    this.dateFormatter = new DateFormattingService()
  }

  /**
   * Render complete inspection section
   * Returns the Y position after rendering
   * @param remarksMinHeight - Optional minimum height for remarks section (to fill page space)
   */
  render(
    doc: jsPDF,
    inspection: ControlCardInspection,
    mainEquipment: ControlCardEquipment,
    startY: number,
    remarksMinHeight?: number
  ): number {
    let currentY = startY

    // 1. Remarks section (with optional expanded height to fill space)
    currentY = this.renderRemarks(doc, inspection, currentY, remarksMinHeight)

    // 2. Regulatory text
    currentY = this.renderRegulatoryText(doc, currentY)

    // 3. Test/Control information
    currentY = this.renderTestControl(doc, inspection, mainEquipment, currentY)

    // 4. Inspection details
    currentY = this.renderInspectionDetails(doc, inspection, mainEquipment, currentY)

    // 5. Status codes explanation
    currentY = this.renderStatusCodes(doc, currentY)

    return currentY
  }

  /**
   * Render remarks table
   */
  private renderRemarks(
    doc: jsPDF,
    inspection: ControlCardInspection,
    startY: number,
    minHeight?: number
  ): number {
    return this.tableRenderer.renderRemarksTable(
      doc,
      inspection.remarks || '',
      startY,
      minHeight
    )
  }

  /**
   * Render regulatory text table
   */
  private renderRegulatoryText(
    doc: jsPDF,
    startY: number
  ): number {
    return this.tableRenderer.renderRegulatoryTextTable(doc, startY)
  }

  /**
   * Render test/control information table
   */
  private renderTestControl(
    doc: jsPDF,
    inspection: ControlCardInspection,
    mainEquipment: ControlCardEquipment,
    startY: number
  ): number {
    // Compose "Testet/kontrollert iht." — the regulatory prefix is always
    // included; the standards portion is the user's picker selection
    // (`controlledAgainst`) when provided, falling back to the equipment's own
    // production standard.
    const userStandards = inspection.controlledAgainst?.trim()
    const fallbackStandards = this.getProductionStandard(mainEquipment)?.trim() || ''
    const standardsPart = userStandards || fallbackStandards
    const testedAccordingTo = standardsPart
      ? `Forskrift Nr 1357, Forskrift Nr 1360 & ${standardsPart}`
      : `Forskrift Nr 1357, Forskrift Nr 1360`

    // Use checklist from category if available, otherwise fall back to category-based default
    let checklistValue = mainEquipment.checklist
    if (!checklistValue) {
      // Fallback: F-category = "Offshore lastebærere", other categories = "Løfteutstyr"
      const isFCategory = mainEquipment.category?.toUpperCase().startsWith('F') ?? false
      checklistValue = isFCategory ? 'Offshore lastebærere' : 'Løfteutstyr'
    }

    return this.tableRenderer.renderTestControlTable(
      doc,
      testedAccordingTo,
      checklistValue,
      startY
    )
  }

  /**
   * Render inspection details table
   */
  private renderInspectionDetails(
    doc: jsPDF,
    inspection: ControlCardInspection,
    mainEquipment: ControlCardEquipment,
    startY: number
  ): number {
    // Format exam date
    const examDate = this.dateFormatter.formatNorwegianDate(
      inspection.performedDate || new Date().toISOString()
    )

    // Translate job type
    const examType = this.translateJobType(inspection.jobType)

    // Map status
    const status = this.mapStatus(inspection.status)

    // Use validUntil if provided, otherwise calculate next exam date
    // Format as MM-YYYY (e.g., "12-2026") for control card display
    const nextExam = inspection.validUntil 
      ? this.dateFormatter.formatMonthYear(inspection.validUntil)
      : this.dateFormatter.calculateNextInspectionDate(
          inspection.performedDate,
          mainEquipment.category || ''
        )

    return this.tableRenderer.renderInspectionTable(
      doc,
      examDate,
      examType,
      status,
      nextExam,
      startY
    )
  }

  /**
   * Render status codes explanation table
   */
  private renderStatusCodes(
    doc: jsPDF,
    startY: number
  ): number {
    return this.tableRenderer.renderStatusCodesTable(doc, startY)
  }

  /**
   * Get production standard from equipment specifications
   */
  private getProductionStandard(equipment: ControlCardEquipment): string | null {
    // Try to find production standard specification
    const prodStandard = equipment.specifications.findByKey('prod_acc_to') ||
      equipment.specifications.findByKey('production_standard_id') ||
      equipment.specifications.findByKey('standard')

    if (prodStandard) {
      // Format the value (already resolved from UUID if needed)
      return prodStandard.value
    }

    return null
  }

  /**
   * Translate job type to Norwegian
   */
  private translateJobType(jobType: string): string {
    return JOB_TYPE_TRANSLATIONS[jobType] || jobType
  }

  /**
   * Map status to display string
   */
  private mapStatus(status: string): string {
    return STATUS_MAPPINGS[status] || 'OK - Godkjent/Approved'
  }
}
